import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import { IScormListItemFragment, ScormsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useScormFilter } from 'training/hooks/useScormFilter';

export const useScormList = (filterParamsInUrl = true) => {
    const { showApolloError } = useApolloError();

    const { filterValues, handleSearch: onSearch } =
        useScormFilter(filterParamsInUrl);
    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    // Build variables for query
    const variables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValues,
        }),
        [paginationVariables, filterValues]
    );

    const { loading, error, data } = useQuery(ScormsDocument, {
        variables,
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const scormsObject = data?.scorms;
    const scormsCount = scormsObject?.count;
    const scorms = useMemo(
        () =>
            (scormsObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as IScormListItemFragment[]) || [],
        [scormsObject?.edges]
    );

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(scormsCount || 0);
    }, [scormsCount, loading, initializePagination]);

    return {
        scorms,
        scormsCount,
        filterValues,
        loading,
        error,
        setPage,
        paginationSettings,
        onSearch,
    };
};
